import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BackofficeLayout } from '../BackofficeLayout';
import {
  Alert,
  Button,
  Icon,
  Input,
  PageHeading,
  Pills,
  Section,
  Stepper,
  Table,
  Text,
} from 'barte-design-system';
import type { ColumnDefinition, SortDirection, SortState } from 'barte-design-system';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircleIcon,
  Copy,
  Plus,
  Storefront,
  Trash,
} from '@phosphor-icons/react';
import { MccTagInput } from '../CadastroPlanoTaxas/MccTagInput';
import { MultiSelectTipos } from './MultiSelectTipos';
import {
  COMPANY_MOCK,
  INITIAL_PLANOS,
  INITIAL_SELLERS_BY_PLANO,
  formatBR,
  todayISO,
  type PlanoTaxa,
} from '../CadastroPlanoTaxas/sharedData';
import { SpreadsheetGrid, makeEmptyGrid, type SpreadsheetValue } from './SpreadsheetGrid';
import { TaxasCartaoGrid, type TaxasCartaoState } from './TaxasCartaoGrid';
import './TaxasCompanyCopiaCola.css';

// ─── Types ────────────────────────────────────────────────────────────────────

type View = 'list' | 'wizard';
type WizardStep = 1 | 2 | 3;

interface GrupoCartao {
  id: string;
  nome?: string; // nome custom — quando undefined, usa "MCCs - Grupo N"
  taxasCartao: TaxasCartaoState; // valores por linha (range ou parcela individual)
  expandedRanges: Record<string, boolean>; // ranges expandidos no grid
  mccs: string[];
  tipos: string[]; // ['online', 'pos', 'tap']
  isDefault: boolean;
}

const TIPO_OPTIONS = [
  { value: 'online', label: 'Online' },
  { value: 'pos',    label: 'POS' },
  { value: 'tap',    label: 'Tap' },
];

// Seção "Taxas Base" — 5 linhas × 4 colunas.
// Colunas 0 e 2 são rótulos fixos (não-editáveis, fundo cinza).
// Colunas 1 e 3 são valores que a pessoa preenche (ou cola da planilha).
const TAXAS_BASE_ROWS = 5;
const TAXAS_BASE_COLS = 4;
const TAXAS_BASE_LABELS_COL_0 = ['PIX', 'PIX Limite', 'PIX Tipo', 'Boleto', 'Débito'];
const TAXAS_BASE_LABELS_COL_2 = ['3DS', 'Repasse', 'Antifraude', 'Processamento', '% CDI'];

function makeTaxasBaseInicial(): SpreadsheetValue {
  const grid = makeEmptyGrid(TAXAS_BASE_ROWS, TAXAS_BASE_COLS);
  for (let r = 0; r < TAXAS_BASE_ROWS; r++) {
    grid[r][0] = TAXAS_BASE_LABELS_COL_0[r] ?? '';
    grid[r][2] = TAXAS_BASE_LABELS_COL_2[r] ?? '';
  }
  return grid;
}

const TAXAS_BASE_LOCKED: boolean[][] = Array.from({ length: TAXAS_BASE_ROWS }, () =>
  [true, false, true, false],
);

const STEPS = [
  { label: 'Input de taxas' },
  { label: 'Revisão' },
  { label: 'Processamento' },
];

function newGroup(isDefault = false): GrupoCartao {
  return {
    id: `grp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    taxasCartao: {},
    expandedRanges: {},
    mccs: [],
    tipos: [],
    isDefault,
  };
}

function countFilled(grid: SpreadsheetValue, locked?: boolean[][]): number {
  let n = 0;
  for (let r = 0; r < grid.length; r++) {
    for (let c = 0; c < (grid[r]?.length ?? 0); c++) {
      if (locked?.[r]?.[c]) continue;
      if ((grid[r][c] ?? '').trim().length > 0) n += 1;
    }
  }
  return n;
}

function countFilledRecord(state: TaxasCartaoState): number {
  let n = 0;
  for (const row of Object.values(state)) {
    for (const v of row) {
      if ((v ?? '').trim().length > 0) n += 1;
    }
  }
  return n;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function TaxasCompanyCopiaCola() {
  const navigate = useNavigate();
  const [view, setView] = useState<View>('list');
  const [planos, setPlanos] = useState<PlanoTaxa[]>(INITIAL_PLANOS);
  const [novoPlanoId, setNovoPlanoId] = useState<number | null>(null);

  // Lista
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [sortState, setSortState] = useState<SortState>({ key: 'dataCriacao', direction: 'desc' });
  const [sellersByPlano] = useState<Record<number, string[]>>(INITIAL_SELLERS_BY_PLANO);

  // Wizard
  const [step, setStep] = useState<WizardStep>(1);
  const [nomePlano, setNomePlano] = useState('');
  const [taxasBase, setTaxasBase] = useState<SpreadsheetValue>(makeTaxasBaseInicial);
  const [groups, setGroups] = useState<GrupoCartao[]>([newGroup(true)]);
  const [showStepErrors, setShowStepErrors] = useState(false);
  const [processingPhase, setProcessingPhase] = useState<0 | 1 | 2>(0);
  const [showSuccess, setShowSuccess] = useState(false);

  function updateGroup(id: string, patch: Partial<GrupoCartao>) {
    setGroups((prev) => prev.map((g) => (g.id === id ? { ...g, ...patch } : g)));
  }

  function setGroupAsDefault(id: string) {
    setGroups((prev) => prev.map((g) => ({ ...g, isDefault: g.id === id })));
  }

  function addNewGroup() {
    setGroups((prev) => [...prev, newGroup(false)]);
  }

  function removeGroup(id: string) {
    setGroups((prev) => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((g) => g.id !== id);
      // se removeu o default, marca o primeiro como default
      if (!next.some((g) => g.isDefault) && next[0]) next[0].isDefault = true;
      return next;
    });
  }

  function duplicateGroup(id: string) {
    setGroups((prev) => {
      const idx = prev.findIndex((g) => g.id === id);
      if (idx < 0) return prev;
      const source = prev[idx];
      const clone: GrupoCartao = {
        id: `grp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        // deep copy dos valores e ranges expandidos
        taxasCartao: Object.fromEntries(
          Object.entries(source.taxasCartao).map(([k, v]) => [k, v.slice()]),
        ),
        expandedRanges: { ...source.expandedRanges },
        mccs: source.mccs.slice(),
        tipos: source.tipos.slice(),
        isDefault: false, // o duplicado nunca herda o default
      };
      const next = prev.slice();
      next.splice(idx + 1, 0, clone);
      return next;
    });
  }

  // ─── List actions ───────────────────────────────────────────────────────────

  function openWizard() {
    setView('wizard');
    setStep(1);
    setNomePlano('');
    setTaxasBase(makeTaxasBaseInicial());
    setGroups([newGroup(true)]);
    setShowStepErrors(false);
    setProcessingPhase(0);
    setShowSuccess(false);
  }

  function returnToList() {
    setView('list');
  }

  // ─── Step 1 validation ──────────────────────────────────────────────────────

  const step1Errors = useMemo(() => {
    const errs: { nome?: string; taxasBase?: string; grupos?: string } = {};
    if (!nomePlano.trim()) errs.nome = 'Informe o nome do plano.';
    if (countFilled(taxasBase, TAXAS_BASE_LOCKED) < 3) errs.taxasBase = 'Preencha as taxas base (mínimo 3 células).';
    const grupoSemTaxa = groups.some((g) => countFilledRecord(g.taxasCartao) < 3);
    const grupoSemMcc = groups.some((g) => g.mccs.length === 0);
    if (grupoSemTaxa) errs.grupos = 'Cada grupo precisa ter taxas de cartão preenchidas (mínimo 3 células).';
    else if (grupoSemMcc) errs.grupos = 'Cada grupo precisa ter ao menos um MCC.';
    return errs;
  }, [nomePlano, taxasBase, groups]);

  const step1Valid = Object.keys(step1Errors).length === 0;

  // ─── Wizard navigation ──────────────────────────────────────────────────────

  function handleNext() {
    if (step === 1) {
      if (!step1Valid) { setShowStepErrors(true); return; }
      setShowStepErrors(false);
      setStep(2);
    } else if (step === 2) {
      setStep(3);
    }
  }

  function handlePrev() {
    if (step === 2) setStep(1);
    else if (step === 1) returnToList();
  }


  // ─── Processamento ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (view !== 'wizard' || step !== 3) return;
    setProcessingPhase(0);
    setShowSuccess(false);
    const t1 = setTimeout(() => setProcessingPhase(1), 3000);
    const t2 = setTimeout(() => {
      setProcessingPhase(2);
      // Cria o plano no estado da lista
      const novoId = Math.max(...planos.map((p) => p.id)) + 1;
      const novo: PlanoTaxa = {
        id: novoId,
        nome: nomePlano.trim(),
        tipoAntecipado: false,
        tipoFluxoTempo: false,
        qtdSellers: 0,
        dataCriacao: todayISO(),
      };
      setPlanos((prev) => [...prev, novo]);
      setNovoPlanoId(novoId);
    }, 6000);
    const t3 = setTimeout(() => setShowSuccess(true), 7500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [view, step]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Totais agregados ───────────────────────────────────────────────────────

  const totalTaxas = useMemo(() => {
    const base = 24 * 5 * 6 + 3 * 6; // 738
    return groups.reduce((sum, g) => sum + base * Math.max(g.mccs.length, 1), 0);
  }, [groups]);

  // ─── List rendering ─────────────────────────────────────────────────────────

  const columns: ColumnDefinition<PlanoTaxa>[] = [
    { key: 'id', title: 'ID', dataIndex: 'id', width: 70, render: (v) => <Text variant="body-sm">{v as number}</Text> },
    {
      key: 'nome',
      title: 'Planos de taxas',
      dataIndex: 'nome',
      sortable: true,
      render: (v) => <Text variant="body-sm">{v as string}</Text>,
    },
    {
      key: 'dataCriacao',
      title: 'Data de criação',
      dataIndex: 'dataCriacao',
      width: 140,
      sortable: true,
      render: (v) => <Text variant="body-sm">{formatBR(v as string)}</Text>,
    },
    {
      key: 'planoAntecipado',
      title: 'Plano Antecipado',
      dataIndex: 'tipoAntecipado',
      width: 170,
      render: (_v, r) => r.isDefaultAntecipado
        ? <Pills label="Plano default" state="success" variant="fill" size="sm" />
        : <Text variant="body-sm">—</Text>,
    },
    {
      key: 'planoFluxoTempo',
      title: 'Plano Fluxo do tempo',
      dataIndex: 'tipoFluxoTempo',
      width: 180,
      render: (_v, r) => r.isDefaultFluxoTempo
        ? <Pills label="Plano default" state="success" variant="fill" size="sm" />
        : <Text variant="body-sm">—</Text>,
    },
    {
      key: 'qtdSellers',
      title: 'Qtd. Sellers',
      dataIndex: 'qtdSellers',
      width: 120,
      render: (_v, r) => {
        const count = sellersByPlano[r.id]?.length ?? 0;
        return (
          <Pills
            label={`${count} seller${count !== 1 ? 's' : ''}`}
            state={count > 0 ? 'info' : 'default'}
            variant="light"
            size="sm"
            icon={Storefront}
          />
        );
      },
    },
  ];

  const sortedPlanos = useMemo(() => {
    if (!sortState.direction) return planos;
    const arr = [...planos];
    arr.sort((a, b) => {
      let av: string | number = '';
      let bv: string | number = '';
      if (sortState.key === 'nome') {
        av = a.nome.toLowerCase();
        bv = b.nome.toLowerCase();
      } else if (sortState.key === 'dataCriacao') {
        av = a.dataCriacao;
        bv = b.dataCriacao;
      }
      if (av < bv) return sortState.direction === 'asc' ? -1 : 1;
      if (av > bv) return sortState.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [planos, sortState]);

  const pagedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedPlanos.slice(start, start + pageSize);
  }, [page, sortedPlanos]);
  const totalPages = Math.ceil(sortedPlanos.length / pageSize);

  function handleSort(key: string, direction: SortDirection) {
    setSortState({ key, direction });
    setPage(1);
  }

  function renderListView() {
    return (
      <>
        <PageHeading
          title={COMPANY_MOCK.nome}
          primaryActionLabel="Novo plano"
          onPrimaryAction={openWizard}
        />

        <div className="tcc-company-pills-row">
          <Pills label={`ID: ${COMPANY_MOCK.id}`} state="default" variant="light" size="sm" />
          <Pills label={`CNPJ: ${COMPANY_MOCK.cnpj}`} state="default" variant="light" size="sm" />
        </div>

        <div className="content-table tcc-content-table">
          <Table<PlanoTaxa>
            data={pagedData}
            columns={columns}
            rowKey={(r) => r.id}
            emptyText="Nenhum plano cadastrado."
            sortState={sortState}
            onSort={handleSort}
            onRowClick={(r) => navigate(`/backoffice/taxas-company/plano/${r.id}`)}
            pagination={{
              currentPage: page,
              totalPages,
              totalItems: sortedPlanos.length,
              onPageChange: setPage,
            }}
          />
        </div>
      </>
    );
  }

  // ─── Wizard renders ─────────────────────────────────────────────────────────

  function renderStep1() {
    return (
      <div className="tcc-step-body">
        <Input
          label="Nome do plano"
          placeholder="Ex.: Plano antecipado Go Pagos"
          value={nomePlano}
          onChange={(e) => setNomePlano((e.target as HTMLInputElement).value)}
          error={showStepErrors && step1Errors.nome ? step1Errors.nome : false}
        />

        {/* ── Taxas ──────────────────────────────────────────────────── */}
        <div className="tcc-field">
          <Text variant="label-md" className="tcc-field-label">Taxas</Text>
          <SpreadsheetGrid
            rows={TAXAS_BASE_ROWS}
            cols={TAXAS_BASE_COLS}
            hideColHeaders
            hideRowHeaders
            lockedCells={TAXAS_BASE_LOCKED}
            value={taxasBase}
            onChange={setTaxasBase}
          />
          {showStepErrors && step1Errors.taxasBase && (
            <Text variant="caption-md" className="tcc-error-text">{step1Errors.taxasBase}</Text>
          )}
        </div>

        {/* ── Taxas Cartão de Crédito (por grupo) ─────────────────────── */}
        <div className="tcc-field">
          {groups.map((g, idx) => (
            <div key={g.id} className="tcc-group-block">
              <div className="tcc-group-header">
                <div className="tcc-group-header-label">
                  <Text variant="label-md" className="tcc-field-label">
                    Taxas Cartão de Crédito
                  </Text>
                  {(() => {
                    const displayName = g.nome ?? `MCCs - Grupo ${idx + 1}`;
                    return (
                      <input
                        type="text"
                        className="tcc-group-name-pill"
                        value={displayName}
                        onChange={(e) => updateGroup(g.id, { nome: e.target.value })}
                        onBlur={(e) => {
                          // Se vazio, volta pro default
                          if (e.target.value.trim() === '') updateGroup(g.id, { nome: undefined });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                        }}
                        style={{ width: `${Math.max(displayName.length + 1, 12)}ch` }}
                        aria-label={`Editar nome do grupo ${idx + 1}`}
                      />
                    );
                  })()}
                </div>
                <div className="tcc-group-actions">
                  <button
                    type="button"
                    className={`tcc-radio-btn${g.isDefault ? ' is-active' : ''}`}
                    onClick={() => setGroupAsDefault(g.id)}
                    aria-pressed={g.isDefault}
                  >
                    <span className="tcc-radio-bullet" />
                    {g.isDefault ? 'Grupo Default' : 'Definir como Grupo Default'}
                  </button>
                  <button
                    type="button"
                    className="tcc-group-icon-btn"
                    onClick={() => duplicateGroup(g.id)}
                    aria-label={`Duplicar Grupo ${idx + 1}`}
                    title="Duplicar grupo"
                  >
                    <Icon icon={Copy} size={16} weight="regular" />
                  </button>
                  {groups.length > 1 && (
                    <button
                      type="button"
                      className="tcc-group-icon-btn tcc-group-icon-btn--danger"
                      onClick={() => removeGroup(g.id)}
                      aria-label={`Remover Grupo ${idx + 1}`}
                      title="Remover grupo"
                    >
                      <Icon icon={Trash} size={16} weight="regular" />
                    </button>
                  )}
                </div>
              </div>

              <TaxasCartaoGrid
                value={g.taxasCartao}
                onChange={(v) => updateGroup(g.id, { taxasCartao: v })}
                expanded={g.expandedRanges}
                onToggleExpand={(rangeKey) =>
                  updateGroup(g.id, {
                    expandedRanges: {
                      ...g.expandedRanges,
                      [rangeKey]: !g.expandedRanges[rangeKey],
                    },
                  })
                }
              />

              <div className="tcc-mcc-row">
                <div className="tcc-mcc-row-tags">
                  <MccTagInput
                    value={g.mccs}
                    onChange={(v) => updateGroup(g.id, { mccs: v })}
                    error={
                      showStepErrors && g.mccs.length === 0
                        ? 'Adicione ao menos um MCC ao grupo.'
                        : undefined
                    }
                  />
                </div>
                <div className="tcc-mcc-row-default">
                  <MultiSelectTipos
                    label="Tipo"
                    placeholder="Selecione"
                    options={TIPO_OPTIONS}
                    value={g.tipos}
                    onChange={(v) => updateGroup(g.id, { tipos: v })}
                  />
                </div>
              </div>

              {idx < groups.length - 1 && <div className="tcc-group-divider" />}
            </div>
          ))}

          {showStepErrors && step1Errors.grupos && (
            <Text variant="caption-md" className="tcc-error-text">{step1Errors.grupos}</Text>
          )}

          <div className="tcc-group-divider" />

          <div className="tcc-add-group-wrap">
            <Button
              variant="outline"
              size="sm"
              iconLeft={<Icon icon={Plus} size={16} weight="bold" />}
              onClick={addNewGroup}
            >
              Taxas de Cartão de Crédito
            </Button>
          </div>
        </div>
      </div>
    );
  }

  function renderStep2() {
    return (
      <div className="tcc-step-body">
        <Section title="Plano">
          <Section.Row label="Nome do plano" content={<Text variant="label-sm">{nomePlano}</Text>} />
          <Section.Row
            label="Empresa"
            content={<Text variant="label-sm">{COMPANY_MOCK.nome}</Text>}
          />
          <Section.Row
            label="Total de grupos de cartão"
            content={<Text variant="label-sm">{groups.length}</Text>}
          />
          <Section.Row
            label="Total estimado de taxas"
            content={<Text variant="label-sm">{totalTaxas.toLocaleString('pt-BR')} taxas</Text>}
          />
        </Section>

        {groups.map((g, idx) => (
          <Section
            key={g.id}
            title={`Grupo ${idx + 1}${g.isDefault ? ' · default' : ''}`}
          >
            <Section.Row
              label="MCCs"
              content={
                <div className="tcc-mcc-pills">
                  {g.mccs.map((code) => (
                    <Pills key={code} label={code} state="default" variant="light" size="sm" />
                  ))}
                </div>
              }
            />
            <Section.Row
              label="Tipos"
              content={
                <div className="tcc-mcc-pills">
                  {g.tipos.length === 0
                    ? <Text variant="label-sm">—</Text>
                    : g.tipos.map((t) => {
                        const opt = TIPO_OPTIONS.find((o) => o.value === t);
                        return (
                          <Pills
                            key={t}
                            label={opt?.label ?? t}
                            state="accent"
                            variant="light"
                            size="sm"
                          />
                        );
                      })}
                </div>
              }
            />
          </Section>
        ))}

        <Alert
          variant="info"
          title="As variações de taxas serão geradas automaticamente:"
          description="A partir da base de cálculo e para cada grupo: Parcela (1 a 24), bandeira (Mastercard, Visa, Elo, Amex, Hiper), dias de repasse (D+1, D+2, D+3, D+10, D+14, D+30) e método de pagamento."
          showCloseButton={false}
        />
      </div>
    );
  }

  function renderStep3() {
    const phases = [
      'Calculando variações de taxa…',
      'Processando cadastro…',
      'Tudo pronto!',
    ];
    if (showSuccess) {
      const CONFETTI_COLORS = ['#EF3B7D', '#21A23E', '#1D70B8', '#F5A623', '#7B61FF'];
      const confettiCount = 14;
      return (
        <div className="tcc-state-screen">
          <div className="tcc-success-wrap">
            <Icon icon={CheckCircleIcon} size={56} weight="fill" className="tcc-success-icon" />
            <div className="tcc-confetti" aria-hidden>
              {Array.from({ length: confettiCount }).map((_, i) => {
                const angle = (360 / confettiCount) * i;
                const distance = 56 + (i % 3) * 12;
                const color = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
                const delay = (i % 5) * 40;
                return (
                  <span
                    key={i}
                    className="tcc-confetti-piece"
                    style={{
                      ['--angle' as any]: `${angle}deg`,
                      ['--distance' as any]: `${distance}px`,
                      ['--delay' as any]: `${delay}ms`,
                      background: color,
                    }}
                  />
                );
              })}
            </div>
          </div>
          <Text variant="body-xl" className="tcc-state-screen-title">
            Plano "{nomePlano}" criado com sucesso
          </Text>
          <Pills
            label={`${totalTaxas.toLocaleString('pt-BR')} taxas cadastradas em ${groups.length} grupo${groups.length !== 1 ? 's' : ''} de cartão`}
            state="success"
            variant="light"
            size="base"
          />
        </div>
      );
    }
    return (
      <div className="tcc-state-screen">
        <div className="tcc-spinner" aria-label="Processando" />
        <Text variant="body-xl" className="tcc-state-screen-title" role="status" aria-live="polite">
          {phases[processingPhase]}
        </Text>
      </div>
    );
  }

  function renderWizardFooter() {
    if (step === 3) {
      if (showSuccess) {
        return (
          <>
            <div />
            <div className="tcc-footer-right">
              <Button variant="outline" onClick={returnToList}>
                Voltar para planos
              </Button>
              <Button
                onClick={() => {
                  if (novoPlanoId) navigate(`/backoffice/taxas-company/plano/${novoPlanoId}`);
                  else returnToList();
                }}
              >
                Ver plano
              </Button>
            </div>
          </>
        );
      }
      return null;
    }
    return (
      <>
        <Button
          variant="ghost"
          onClick={handlePrev}
          iconLeft={<Icon icon={ArrowLeft} size={18} weight="regular" />}
        >
          {step === 1 ? 'Cancelar' : 'Voltar'}
        </Button>
        <Button
          onClick={handleNext}
          iconRight={<Icon icon={ArrowRight} size={18} weight="regular" />}
        >
          {step === 2 ? 'Confirmar e processar' : 'Próximo'}
        </Button>
      </>
    );
  }

  function renderWizardView() {
    return (
      <>
        <PageHeading title="Novo plano de taxas" />

        <div className="tcc-company-pill-below-title">
          <Pills
            label={`Empresa: ${COMPANY_MOCK.nome}`}
            state="default"
            variant="light"
            size="sm"
          />
        </div>

        <div className="tcc-stepper">
          <Stepper steps={STEPS} currentStep={step} />
        </div>

        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}

        <div className="tcc-footer">
          {renderWizardFooter()}
        </div>
      </>
    );
  }

  return (
    <BackofficeLayout activeItem="taxas-company-copia-cola">
      <main className="content">
        <div className="content-inner tcc-page">
          {view === 'list' ? renderListView() : renderWizardView()}
        </div>
      </main>
    </BackofficeLayout>
  );
}
